import SendInviteIcon from '@mui/icons-material/Email';
import { Tooltip } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { Confirm, useNotify, useRecordContext, useRefresh, useTranslate } from 'react-admin';

import { PutB2bEmployeesSendActivatedInvitationIdEndpoint } from '@Common/types/b2b/invitations';
import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';

import { Button } from '@sh4res/bo-common/components/Button';
import { useResourceBasePath, useUpdatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

export const B2bEmployeeSendInviteButton = ({ testId }: { testId: string }) => {
  const record = useRecordContext<GetB2bEmployeesOneResponse>();
  const [update] = useUpdatePromise<
    Record<string, never>,
    PutB2bEmployeesSendActivatedInvitationIdEndpoint['Client']['ResponseBody']
  >();
  const notify = useNotify();
  const refresh = useRefresh();
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();
  const [sendInviteConfirmOpen, setSendInviteConfirmOpen] = useState<boolean>(false);

  const openSendInviteConfirmModal = useCallback(() => setSendInviteConfirmOpen(true), []);
  const closeSendInviteConfirmModal = useCallback(() => setSendInviteConfirmOpen(false), []);

  const { isDisabled, tooltipTitle } = useMemo(() => {
    if (!record) {
      return { isDisabled: true, tooltipTitle: '' };
    }
    if (record.kyb_status !== 'verified') {
      return {
        isDisabled: true,
        tooltipTitle: translate(
          'resources.bo_common.b2b.employees.actions.send_invite.disabled_company_not_verified',
        ),
      };
    }
    if (record.is_activated_by_company && record.activation_code_is_used) {
      return {
        isDisabled: true,
        tooltipTitle: translate(
          'resources.bo_common.b2b.employees.actions.send_invite.disabled_already_activated',
        ),
      };
    }
    if (record.is_user_closed_or_archived) {
      return {
        isDisabled: true,
        tooltipTitle: translate(
          'resources.bo_common.b2b.employees.actions.send_invite.disabled_user_closed',
        ),
      };
    }
    return { isDisabled: false, tooltipTitle: '' };
  }, [record, translate]);

  const confirmContent = useMemo(() => {
    const baseMessage = translate('resources.bo_common.b2b.employees.actions.send_invite.confirm');
    if (!record?.email) {
      const paperMailMessage = translate(
        'resources.bo_common.b2b.employees.actions.send_invite.confirm_paper_mail',
      );
      return `${baseMessage}\n\n${paperMailMessage}`;
    }
    return baseMessage;
  }, [record?.email, translate]);

  const handleSendInvite = useCallback(async () => {
    try {
      if (!record) {
        return null;
      }
      await update(`${resourceBasePath}employees/send_activated_invitation`, {
        id: record.id,
        data: {},
      });
      notify('resources.bo_common.b2b.employees.actions.send_invite.success', {
        type: 'success',
      });
      setSendInviteConfirmOpen(false);
      refresh();
    } catch (error: unknown) {
      notify('resources.bo_common.b2b.employees.actions.send_invite.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    }
  }, [record, notify, update, refresh, resourceBasePath]);

  if (!record) {
    return null;
  }

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <span style={{ display: 'inline-block' }}>
          <Button
            disabled={isDisabled}
            onClick={openSendInviteConfirmModal}
            sx={{
              height: '30px',
              fontWeight: 'bold',
            }}
            size="small"
            data-testid={testId}
            label="resources.bo_common.b2b.employees.actions.send_invite.label"
            permissions={{
              resource: `${resourceBasePath}employees/send_activated_invitation`,
              action: 'edit',
            }}>
            <SendInviteIcon />
          </Button>
        </span>
      </Tooltip>
      <Confirm
        isOpen={sendInviteConfirmOpen}
        title="Confirmation"
        content={confirmContent}
        onConfirm={handleSendInvite}
        onClose={closeSendInviteConfirmModal}
      />
    </>
  );
};
